import { type Action, createStore } from 'react-sweet-state';

import type { Customer } from 'src/data/customer';
import type { User } from 'src/data/user';
import type { Workspace } from 'src/data/workspace';
import {
  establishSessionForOAuth,
  queryCurrentUser,
  queryOAuthApplicationName,
} from 'src/graphql/user';
// @ts-expect-error - no TypeScript definitions yet
import { queryWorkspaces } from 'src/graphql/workspace';
import { captureException, captureMessage } from 'src/lib/sentry';

const NAME = 'page-oauth-consent';

export type State = {
  approving: boolean;
  appDisplayName: string | null;
  appNameLoading: boolean;
  appNameError: string | null;
  copilotAccountName: string | null;
  copilotWorkspaceName: string | null;
  infoLoading: boolean;
  returnTo: string | null;
};

export type Actions = typeof ACTIONS;

const getClientIdFromOAuth2Consent = (oauth2Consent: string): string | null => {
  try {
    const url = new URL(oauth2Consent);
    return url.searchParams.get('client_id') || null;
  } catch {
    return null;
  }
};

const getAccountsFromMe = (me: User): Customer[] => {
  const edges = (me as any)?.accounts?.edges;
  if (!Array.isArray(edges)) return [];
  return edges.map((edge: any) => edge.node).filter(Boolean);
};

const INITIAL_STATE: State = {
  approving: false,
  appDisplayName: null,
  appNameLoading: true,
  appNameError: null,
  copilotAccountName: null,
  copilotWorkspaceName: null,
  infoLoading: true,
  returnTo: null,
};

const ACTIONS = {
  initPage:
    (returnTo: string): Action<State> =>
    ({ setState, dispatch }) => {
      setState({ returnTo });
      dispatch(ACTIONS.fetchAppName(returnTo));
      dispatch(ACTIONS.fetchCopilotInfo());
    },

  fetchAppName:
    (returnTo: string): Action<State> =>
    ({ setState }) => {
      const clientId = getClientIdFromOAuth2Consent(returnTo);
      setState({ appNameLoading: true, appNameError: null, appDisplayName: null });

      if (!clientId) {
        setState({
          appNameLoading: false,
          appNameError: 'Invalid request: no application specified.',
        });
        return;
      }

      queryOAuthApplicationName(clientId)
        .then((name: string) => {
          const trimmed = name && name.trim() ? name.trim() : null;
          setState({
            appDisplayName: trimmed,
            appNameError: trimmed ? null : 'Application not found.',
          });
        })
        .catch(() => {
          setState({ appNameError: 'Could not load application details.' });
        })
        .finally(() => setState({ appNameLoading: false }));
    },

  fetchCopilotInfo:
    (): Action<State> =>
    async ({ setState }) => {
      setState({ infoLoading: true });
      try {
        const me = await queryCurrentUser({ credentials: 'include' });
        const settings = (me as User)?.settings;
        const copilotCustomerId = settings?.copilotCustomerId;
        const copilotWorkspaceId = settings?.copilotWorkspaceId;

        const accounts = getAccountsFromMe(me);

        // Resolve account name
        let accountName: string | null = null;
        let customerId = copilotCustomerId;
        if (copilotCustomerId) {
          const account = accounts.find((a) => String(a.id) === String(copilotCustomerId));
          accountName = account?.name || null;
        } else if (accounts.length > 0) {
          accountName = accounts[0].name;
          customerId = String(accounts[0].id);
        }

        // Resolve workspace name
        let workspaceName: string | null = null;
        if (customerId) {
          try {
            const workspaces: Workspace[] = await queryWorkspaces(customerId);
            if (copilotWorkspaceId) {
              const ws = workspaces.find((w) => String(w.id) === String(copilotWorkspaceId));
              workspaceName = ws?.name || null;
            } else if (workspaces.length > 0) {
              workspaceName = workspaces[0].name;
            }
          } catch {
            // Workspace fetch failed — show account only
          }
        }

        setState({ copilotAccountName: accountName, copilotWorkspaceName: workspaceName });
      } catch {
        // User fetch failed — consent page still works, just without names
      } finally {
        setState({ infoLoading: false });
      }
    },

  approve:
    (): Action<State, void, Promise<string | null>> =>
    async ({ getState, setState }) => {
      const { returnTo, approving } = getState();
      if (!returnTo || approving) return null;

      setState({ approving: true });
      try {
        const result = await establishSessionForOAuth(returnTo);
        const redirectUrl: string | undefined = result?.redirectUrl;
        if (!redirectUrl) {
          captureMessage('[OAuth] establishSessionForOAuth did not return redirectUrl', {
            extra: { result },
          });
          setState({ approving: false });
          return null;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 100));
        return redirectUrl;
      } catch (err) {
        captureException(err as Error, { extra: { context: 'establishSessionForOAuth' } });
        setState({ approving: false });
        return null;
      }
    },
};

export const PageStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
